import { useEffect, useState } from "react";
import { TrendingUp, Percent, Users, FileCheck2, PackageSearch, Banknote, ShoppingBag, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatINR } from "@/constants/branding";
import { StatCard } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Pill, DataTable, SectionTitle, Field, fmtDateTime } from "@/components/dash";

/* ------------------------------------------------------------------ overview */
function Bars({ series }) {
  const max = Math.max(1, ...series.map((s) => s.gmv));
  return (
    <div className="flex items-end gap-1.5 h-40" role="img" aria-label="Sales for the last 14 days">
      {series.map((s) => (
        <div key={s.date} className="flex-1 flex flex-col items-center justify-end gap-1 min-w-0 group">
          <div className="w-full rounded-t bg-primary/80 group-hover:bg-primary transition-colors relative" style={{ height: `${Math.max(2, (s.gmv / max) * 100)}%` }}>
            <span className="hidden group-hover:block absolute -top-7 left-1/2 -translate-x-1/2 bg-deep text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap">{formatINR(s.gmv)}</span>
          </div>
          <span className="text-[9px] text-muted-foreground">{s.date.slice(8)}</span>
        </div>
      ))}
    </div>
  );
}

export function MarketOverview({ stats, onNavigate }) {
  if (!stats) return <p className="text-muted-foreground">Loading marketplace stats…</p>;
  const todo = [
    { n: stats.pending_applications, label: "seller application(s) waiting for review", tab: "requests", icon: FileCheck2 },
    { n: stats.pending_products, label: "product(s) waiting for moderation", tab: "moderation", icon: PackageSearch },
    { n: Math.round(stats.payouts_due) > 0 ? 1 : 0, label: `${formatINR(stats.payouts_due)} payable to sellers`, tab: "payouts", icon: Banknote },
  ].filter((t) => t.n > 0);
  return (
    <div className="space-y-6" data-testid="market-overview">
      {todo.length > 0 && (
        <div className="bg-white rounded-xl border border-border divide-y divide-border">
          <p className="px-4 py-3 text-sm font-semibold">Needs your attention</p>
          {todo.map((t) => (
            <button key={t.tab} onClick={() => onNavigate(t.tab)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50" data-testid={`todo-${t.tab}`}>
              <t.icon className="h-5 w-5 text-[#b45309]" />
              <span className="text-sm flex-1">{t.tab === "payouts" ? t.label : <><b>{t.n}</b> {t.label}</>}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Gross sales (GMV)" value={formatINR(stats.gmv)} icon={TrendingUp} />
        <StatCard label="Commission earned" value={formatINR(stats.commission_earned)} icon={Percent} />
        <StatCard label="Orders" value={stats.orders} icon={ShoppingBag} />
        <StatCard label="Active sellers" value={stats.active_vendors} icon={Users} />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-border p-5">
          <p className="font-semibold mb-4">Sales — last 14 days</p>
          <Bars series={stats.series} />
        </div>
        <div className="bg-white rounded-xl border border-border p-5">
          <p className="font-semibold mb-3">Top sellers</p>
          {stats.top_vendors.length === 0 ? <p className="text-sm text-muted-foreground">No sales yet.</p> : (
            <ol className="space-y-2.5">
              {stats.top_vendors.map((v, i) => (
                <li key={v.vendor_id} className="flex items-center gap-3 text-sm"><span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span><span className="flex-1 truncate">{v.name}</span><b>{formatINR(v.gmv)}</b></li>
              ))}
            </ol>
          )}
          <p className="font-semibold mt-6 mb-2">Orders by status</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.orders_by_status).map(([k, n]) => <Pill key={k} tone={k === "delivered" ? "green" : k === "cancelled" ? "slate" : "blue"}>{k}: {n}</Pill>)}
            {Object.keys(stats.orders_by_status).length === 0 && <span className="text-sm text-muted-foreground">—</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ audit log */
const ACTION_TONE = (a) => (a.includes("reject") || a.includes("suspend") || a.includes("delete") ? "red" : a.includes("approve") || a.includes("reinstate") || a.includes("payout") ? "green" : "slate");

export function AuditPanel() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/admin/audit-logs", { params: { limit: 200, action: filter || undefined } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [filter]);
  return (
    <div data-testid="audit-panel">
      <SectionTitle title="Audit log" desc="A permanent record of who did what — approvals, payouts, role changes and more."
        action={<Input className="w-52" placeholder="Filter e.g. vendor, payout" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="audit-filter" />} />
      <DataTable empty="No activity recorded yet." head={["When", "Who", "Action", "Details"]}
        rows={(rows || []).map((l) => [<span className="whitespace-nowrap text-xs">{fmtDateTime(l.created_at)}</span>,
          <div><p className="text-sm font-medium">{l.actor_name}</p><p className="text-xs text-muted-foreground">{l.actor_role}</p></div>,
          <Pill tone={ACTION_TONE(l.action)} className="normal-case">{l.action}</Pill>, <span className="text-sm">{l.summary}</span>])} />
    </div>
  );
}

/* ------------------------------------------------------------------ marketplace settings */
export function MarketSettingsPanel({ isSuper }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/marketplace-settings").then((r) => setS(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!s) return <p className="text-muted-foreground">Loading…</p>;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/marketplace-settings", {
        default_commission: parseFloat(s.default_commission), payout_hold_days: parseInt(s.payout_hold_days, 10), auto_approve_products: s.auto_approve_products });
      setS(data); toast.success("Marketplace settings saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl space-y-5" data-testid="market-settings">
      <SectionTitle title="Marketplace rules" desc={isSuper ? "These apply to every seller on the platform." : "Only a Super Admin can change these."} />
      <div className="bg-white rounded-xl border border-border p-5 space-y-5">
        <Field label="Default commission (%)" hint="Charged on every sale unless a seller has a custom rate. Existing orders keep the rate they were placed with.">
          <Input type="number" min="0" max="50" step="0.5" disabled={!isSuper} value={s.default_commission} onChange={(e) => setS({ ...s, default_commission: e.target.value })} data-testid="set-commission" />
        </Field>
        <Field label="Payout hold (days after delivery)" hint="Earnings are released to the seller's balance only after this return window.">
          <Input type="number" min="0" max="60" disabled={!isSuper} value={s.payout_hold_days} onChange={(e) => setS({ ...s, payout_hold_days: e.target.value })} data-testid="set-hold" />
        </Field>
        <div className="flex items-start justify-between gap-4">
          <div><p className="text-sm font-medium">Auto-approve seller products</p><p className="text-xs text-muted-foreground mt-0.5 max-w-sm">Off (recommended): every new or edited listing is reviewed by your team first. On: products go live instantly.</p></div>
          <Switch disabled={!isSuper} checked={s.auto_approve_products} onCheckedChange={(v) => setS({ ...s, auto_approve_products: v })} data-testid="set-auto" />
        </div>
        {isSuper && <Button onClick={save} disabled={saving} data-testid="set-save">{saving ? "Saving…" : "Save settings"}</Button>}
      </div>
    </div>
  );
}
